import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClickAndLabelTask from '../../SubmitTask/ClickAndLabel/ClickAndLabelTask';
import { ScoreTabs } from '../../../variables/types';
import GoalSection from '../../../components/GoalSection';
import ScoreTaskLayout from '../ScoreTaskLayout';
import BodyContainer from '../BodyContainer';
import FeedbackRow from '../Common/StudentFeedback/FeedbackRow';
import useScore from '../score-hook';
import ScoringHeader from '../Common/ScoringHeader';
import TeacherNotes from '../Common/TeacherNotes';
import ScoringCard from '../Common/ScoringCardWithTooltip';
import WritingBody from './WritingBody';
import { StyledContainer2 } from '../ScoreTask.styled';
import useAdditionalFeedback from '../Common/StudentFeedback/additional-feedback-hook';
import BackdropModal from '../../../components/BackdropModal/BackdropModal';
import PTPromptCard from '../Common/PTPromptCard';
import { NewSubmission } from '../../../generated/graphql';

const Writing = () => {
  const theme = useTheme();
  const {
    id,
    submission,
    setNotes,
    saveNotes,
    showPreview,
    togglePreview,
    notes: teacherNotes,
    loading: scoreLoading,
  } = useScore();

  const {
    loading: additionalFeedbackLoading,
    notes,
    onSave,
    onRecord,
    onDelete,
    audioData,
    isPlaying,
    updateNotes: saveAdditionalFeedback,
    handleStartRecording,
    isRecording,
    setIsPlaying,
  } = useAdditionalFeedback({
    id: id ?? '',
    textFeedback: submission?.getAdditionalFeedback?.feedback_notes ?? '',
    audioFeedback: submission?.getAdditionalFeedback?.audio?.[0] || null,
  });

  const loading = additionalFeedbackLoading || scoreLoading;

  const bodyContent = (
    // TODO - modularize this styling and reuse it for both writing and speaking
    <>
      {/* First Column */}
      <Box>
        <Box>
          <StyledContainer2>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                gridRowGap: theme.spacing(1.25),
              }}
            >
              <Box ml={theme.spacing(2.5)} mt={theme.spacing(2.5)}>
                <Typography variant='sContentHeader'>{submission?.student?.first_name ? `${submission?.student?.first_name}'s Submission` : ''}</Typography>
              </Box>
              <Box>
                <ClickAndLabelTask
                  togglePreview={togglePreview}
                  submission={submission as NewSubmission}
                  annotations={submission?.annotations || []}
                  disableEdit
                  containerHeight="40vh"
                />
              </Box>
            </Box>
          </StyledContainer2>
        </Box>

        <Box mt={theme.spacing(3.75)}>
          <PTPromptCard taskType={ScoreTabs.Writing} />
        </Box>

        <Box mt={theme.spacing(3.75)}>
          <ScoringHeader
            title="Student Writing"
          />
          <WritingBody notes={submission?.writing ?? ''} />
        </Box>

        <Box
          mt={theme.spacing(3.75)}
        >
          <TeacherNotes
            setFeedback={setNotes}
            feedback={teacherNotes}
            submissionStatus={submission?.status ?? ''}
          />
        </Box>
      </Box>

      {/* Second Column */}
      <Box>
        <Box>
          <ScoringCard taskType={ScoreTabs.Writing} />
        </Box>

        <Box mt={theme.spacing(3.75)}>
          <GoalSection goalTitle={ScoreTabs.Writing} />
        </Box>

        <Box
          mt={theme.spacing(3.75)}
        >
          <FeedbackRow
            loading={loading}
            onRecord={onRecord}
            audioData={audioData}
            isPlaying={isPlaying}
            notes={notes}
            updateNotes={saveAdditionalFeedback}
            onDelete={onDelete}
            setIsPlaying={setIsPlaying}
            handleStartRecording={handleStartRecording}
            submissionStatus={submission?.status ?? ''}
          />
        </Box>
      </Box>
      <BackdropModal open={showPreview} onClose={togglePreview}>
        <ClickAndLabelTask
          submission={submission as NewSubmission}
          annotations={submission?.annotations || []}
          disableEdit
          containerHeight="80vh"
        />
      </BackdropModal>
    </>
  );

  return (
    <ScoreTaskLayout
      saveTeacherNotes={saveNotes}
      saveTeacherFeedback={onSave}
      body={<BodyContainer bodyContent={bodyContent} />}
      isRecording={isRecording}
      isPlaying={isPlaying}
    />
  );
};

export default Writing;
